import {
  currentPolicyTypeOptions,
  defaultMotorCurrentInsurerOptions,
  EXPERIENCE_OPTIONS,
} from "@/D2C/features/car-quote/common/static";
import { defaultDriverFormValues } from "@/D2C/features/car-quote/redux/car-quote/carQuoteSlice";
import { CountrySelect, RadioInput, SelectInput } from "@/shared/components";
import DateInput from "@/shared/components/DateInput";
import { useAppDispatch } from "@/shared/redux/store/hooks";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import { driverDetailsSchema } from "@/shared/validation/carQuoteSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import {
  setCurrentInsurers,
  setCurrentPolicyTypes,
  setNoClaimCertificates,
} from "../../redux/dynamic-dropdown";
import { CarDriverFormValues } from "../../redux/type";
import { currentInsurer, currentInsurerType, noClaimCertificate } from "../../services/service";
import type { CarPersonalDetailsAutofillValues } from "../../utils/carPersonalDetailsOcr";

const DEFAULT_SUKOON_INSURER = "Sukoon Insurance (Formerly known as Oman Insurance Company)";

export const DriverDetailsStep = forwardRef<
  StepFormHandle<CarDriverFormValues>,
  StepFormComponentProps<
    CarDriverFormValues,
    {
      autofillValues?: CarPersonalDetailsAutofillValues;
      autofillVersion?: number;
      layoutClassName?: string;
    }
  >
>(function DriverDetailsStep(
  { defaultValues, autofillValues, autofillVersion, layoutClassName },
  ref
) {
  const today = new Date().toISOString().split("T")[0];
  const form = useForm<CarDriverFormValues>({
    resolver: zodResolver(driverDetailsSchema) as any,
    defaultValues: defaultValues ?? defaultDriverFormValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { dynamicCarDropdown } = usePosterReducers();
  const hasActivePolicy = form.watch("hasActivePolicy");
  const claimsLastYear = form.watch("claimsLastYear");
  const currentPolicyType = form.watch("currentPolicyType");
  const currentInsurerValue = form.watch("currentInsurer");

  useEffect(() => {
    form.reset(defaultValues ?? defaultDriverFormValues);
  }, [defaultValues, form]);

  useEffect(() => {
    if (!autofillVersion || !autofillValues?.dateOfBirth) {
      return;
    }

    form.setValue("dateOfBirth", autofillValues.dateOfBirth, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [autofillValues?.dateOfBirth, autofillVersion, form]);

  const resolvedPolicyTypeOptions =
    dynamicCarDropdown?.currentPolicyTypes?.length > 0
      ? dynamicCarDropdown.currentPolicyTypes
      : currentPolicyTypeOptions;

  const resolvedCurrentInsurerOptions =
    dynamicCarDropdown?.currentInsurers?.length > 0
      ? dynamicCarDropdown.currentInsurers
      : defaultMotorCurrentInsurerOptions;

  useImperativeHandle(ref, () => ({
    submit: async () => form.trigger(undefined, { shouldFocus: true }),
    getValues: () => form.getValues(),
  }));

  const dispatch = useAppDispatch();

  useEffect(() => {
    const fetchCurrentPolicyTypes = async () => {
      try {
        if (!hasActivePolicy || dynamicCarDropdown.currentPolicyTypes.length) return;

        const data = await currentInsurerType();
        dispatch(setCurrentPolicyTypes(data));
      } catch (error) {
        console.error("Failed to fetch current policy types:", error);
      }
    };

    const fetchCurrentInsurers = async () => {
      try {
        if (!hasActivePolicy || dynamicCarDropdown.currentInsurers.length) return;

        const data = await currentInsurer();
        dispatch(setCurrentInsurers(data));
      } catch (error) {
        console.error("Failed to fetch current insurers:", error);
      }
    };
    const fetchNoClaimCertificate = async () => {
      try {
        const data = await noClaimCertificate();
        console.log("No Claim Certificates:", data);
        dispatch(setNoClaimCertificates(data));
      } catch (error) {
        console.error("Failed to fetch no claim certificates:", error);
      }
    };

    fetchCurrentPolicyTypes();
    fetchCurrentInsurers();
    fetchNoClaimCertificate();
  }, [
    hasActivePolicy,
    dynamicCarDropdown.currentPolicyTypes.length,
    dynamicCarDropdown.currentInsurers.length,
    dispatch,
  ]);

  useEffect(() => {
    if (!hasActivePolicy) {
      return;
    }

    if (currentPolicyType && !currentInsurerValue) {
      form.setValue("currentInsurer", DEFAULT_SUKOON_INSURER, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [currentInsurerValue, currentPolicyType, form, hasActivePolicy]);

  const formatClassName = layoutClassName ?? "grid grid-cols-1 gap-4 md:grid-cols-2";

  return (
    <div className="space-y-5">
      <div className={formatClassName}>
        <DateInput
          control={form.control}
          name="dateOfBirth"
          label="Date of Birth"
          max={today}
          placeholder="DD/MM/YYYY"
          required
          yearOrder="desc"
        />

        <CountrySelect
          control={form.control}
          name="nationality"
          label="Nationality"
          placeholder="Select Nationality"
          required
        />

        <RadioInput
          control={form.control}
          name="gender"
          label="Gender"
          required
          options={[
            { label: "Male", value: "male" },
            { label: "Female", value: "female" },
          ]}
        />
        <SelectInput
          control={form.control}
          name="uaeDrivingExperience"
          label="Years of driving experience in the UAE"
          options={EXPERIENCE_OPTIONS}
          searchable
          required
        />

        <SelectInput
          control={form.control}
          name="outsideUaeDrivingExperience"
          label="Years of driving experience outside the UAE"
          placeholder="Select Option"
          options={EXPERIENCE_OPTIONS}
          searchable
        />

        <RadioInput
          control={form.control}
          name="hasActivePolicy"
          label="Do you have an active car policy?"
          required
          options={[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ]}
        />

        {hasActivePolicy ? (
          <SelectInput
            control={form.control}
            name="currentPolicyType"
            label="What is your current policy type?"
            options={currentPolicyTypeOptions}
            searchable
            required
          />
        ) : null}
        {hasActivePolicy ? (
          <SelectInput
            control={form.control}
            name="currentInsurer"
            label="Who is your current insurer?"
            options={defaultMotorCurrentInsurerOptions}
            searchable
            required
            disabled={!currentPolicyType}
            className={!currentPolicyType ? "cursor-not-allowed opacity-50" : ""}
          />
        ) : null}

        <RadioInput
          control={form.control}
          name="claimsLastYear"
          label="Did you have any claims last year?"
          required
          options={[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ]}
        />
        {claimsLastYear ? null : (
          <>
            <SelectInput
              control={form.control}
              name="no_claims_certificate"
              label="Can you provide a no claims certificate?"
              options={dynamicCarDropdown?.noClaimCertificates ?? []}
              searchable
              required
            />
          </>
        )}
      </div>
    </div>
  );
});
